import { el } from '../state/store.js';
import {
  vlAddRecipients, vlCampaign, vlCreateCampaign, vlCreateVideo, vlDeleteCampaign, vlDeleteRecipient,
  vlMarkSent, vlOverview, vlSetExpiry, vlUpdateCampaign
} from '../services/video-links.js';
import {
  LINK_FILTERS, byRecentActivity, clock, emailClipboard, fillTemplate, firstName, hueFor, initials,
  linkStatus, matchesFilter, matchesQuery, mediaUrl, recipientsCsv, relativeTime, retentionPoints, watchUrl
} from '../core/video-links.js';
import { formatDate, formatDateTime, pickDate } from './calendar.js';
import { closeX } from './popup.js';
import { toast } from './toast.js';
import { videoUploadBox } from './video-upload.js';

/* THE VIDEO LINKS TAB — one personal link per recipient, and what each did.

   Two screens. The home is the campaigns and the videos, as cards with their
   thumbnails. A campaign is the page you work in: the numbers up top, who kept
   watching, and the people as a list you can scan — a coloured status and a
   progress bar each — with the one action you repeat (Copy email) on the row and
   the rest behind “…”.

   The owner sends every email by hand, so the weight of the screen is the paste:
   “Copy email” puts the thumbnail, wrapped in that person's link, on the
   clipboard as rich content, ready for a Gmail compose window.

   Nothing here talks to the database: see services/video-links.js. */

/* ── small pieces ───────────────────────────────────────────────────────── */

const ICONS = {
  plus: '<path d="M12 5v14M5 12h14"/>',
  back: '<path d="M15 6l-6 6 6 6"/>',
  more: '<circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/>',
  copy: '<rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V6a2 2 0 0 1 2-2h9"/>',
  link: '<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/>',
  mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>',
  refresh: '<path d="M20 11a8 8 0 1 0-2.3 5.7"/><path d="M20 4v7h-7"/>',
  search: '<circle cx="11" cy="11" r="6.5"/><path d="M20 20l-4.2-4.2"/>',
  play: '<path d="M8 5.5v13l11-6.5z" fill="currentColor"/>',
  chevron: '<path d="M9 6l6 6-6 6"/>',
  upload: '<path d="M12 16V4M7 9l5-5 5 5"/><path d="M5 20h14"/>',
  cursor: '<path d="M6 4l12 7-5.2 1.6L11 18z"/>'
};
function icon(name, size = 16) {
  const s = el('span', 'vl2ic');
  s.setAttribute('aria-hidden', 'true');
  s.innerHTML = `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${ICONS[name] || ''}</svg>`;
  return s;
}

/** A button, with an optional leading icon. */
function btn(label, cls = '', ic = null, title = '') {
  const b = el('button', 'btn vl2btn ' + cls);
  b.type = 'button';
  if (ic) b.appendChild(icon(ic, 15));
  if (label) b.appendChild(el('span', null, label));
  if (title) { b.title = title; b.setAttribute('aria-label', label || title); }
  return b;
}
const iconBtn = (ic, title, cls = '') => {
  const b = btn('', 'vl2btn--ic ' + cls, ic, title);
  return b;
};

const field = (label, input, hint = '') => {
  const f = el('label', 'vl2f');
  f.appendChild(el('span', 'vl2f__l', label));
  f.appendChild(input);
  if (hint) f.appendChild(el('span', 'vl2f__h', hint));
  return f;
};
const input = (value = '', placeholder = '', type = 'text') => {
  const i = el('input', 'inp'); i.type = type; i.value = value; i.placeholder = placeholder; return i;
};

/** A dialog on the app's own overlay: Escape and the backdrop close it. */
function dialog(title, body, { wide = false } = {}) {
  const wrap = el('div', 'authwrap');
  const card = el('div', 'authcard vl2dlg' + (wide ? ' vl2dlg--wide' : ''));
  card.appendChild(el('h2', null, title));
  card.appendChild(body);
  const close = () => wrap.remove();
  closeX(card, close);
  wrap.onclick = e => { if (e.target === wrap) close(); };
  wrap.appendChild(card);
  document.body.appendChild(wrap);
  return { close, card };
}

/** A small popover menu under `anchor`. Items: { label, run, danger?, disabled? } or '-'. */
let closeMenu = () => {};
function openMenu(anchor, items) {
  closeMenu();
  const m = el('div', 'vl2menu');
  m.setAttribute('role', 'menu');
  for (const it of items) {
    if (it === '-') { m.appendChild(el('div', 'vl2menu__sep')); continue; }
    const b = el('button', 'vl2menu__item' + (it.danger ? ' is-danger' : ''), it.label);
    b.type = 'button'; b.setAttribute('role', 'menuitem'); b.disabled = !!it.disabled;
    b.onclick = () => { closeMenu(); it.run(); };
    m.appendChild(b);
  }
  document.body.appendChild(m);
  const a = anchor.getBoundingClientRect(), w = m.offsetWidth, h = m.offsetHeight;
  const left = Math.max(8, Math.min(window.innerWidth - w - 8, a.right - w));
  const top = a.bottom + 6 + h > window.innerHeight - 8 ? Math.max(8, a.top - h - 6) : a.bottom + 6;
  m.style.left = left + 'px'; m.style.top = top + 'px';
  const off = e => { if (!m.contains(e.target)) close(); };
  const key = e => { if (e.key === 'Escape') close(); };
  const close = () => {
    m.remove();
    document.removeEventListener('pointerdown', off, true);
    document.removeEventListener('keydown', key, true);
    window.removeEventListener('scroll', close, true);
    window.removeEventListener('resize', close);
    closeMenu = () => {};
  };
  setTimeout(() => document.addEventListener('pointerdown', off, true), 0);
  document.addEventListener('keydown', key, true);
  window.addEventListener('scroll', close, true);
  window.addEventListener('resize', close);
  closeMenu = close;
  m.querySelector('button:not(:disabled)')?.focus();
}

/** Copy a rich (HTML) clipboard, falling back to plain text where the browser cannot. */
async function copyRich({ html, plain }) {
  try {
    await navigator.clipboard.write([new window.ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([plain], { type: 'text/plain' })
    })]);
    return true;
  } catch (_) {
    try { await navigator.clipboard.writeText(plain); return 'plain'; } catch (__) { return false; }
  }
}

/** The thumbnail with the recipient's first name on it, as a data URL.
 *  Needs the media host to allow cross-origin reads (CORS) of the image. */
export async function personalThumbnail(thumbUrl, name) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise((ok, no) => { img.onload = ok; img.onerror = () => no(new Error('The thumbnail could not be loaded for editing — the media host needs to allow cross-origin reads.')); img.src = thumbUrl; });
  const w = Math.min(img.naturalWidth, 1120), h = Math.round(img.naturalHeight * (w / img.naturalWidth));
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const g = c.getContext('2d');
  g.drawImage(img, 0, 0, w, h);
  const label = firstName(name);
  if (label) {
    const size = Math.round(h * 0.075), pad = Math.round(size * 0.55);
    g.font = `600 ${size}px system-ui, -apple-system, "Segoe UI", sans-serif`;
    const tw = g.measureText(label).width, bw = tw + pad * 2, bh = size + pad * 1.2, x = Math.round(w * 0.04), y = h - bh - Math.round(h * 0.05);
    g.fillStyle = 'rgba(0,0,0,.62)';
    g.beginPath(); g.roundRect(x, y, bw, bh, bh / 2); g.fill();
    g.fillStyle = '#fff'; g.textBaseline = 'middle';
    g.fillText(label, x + pad, y + bh / 2 + 1);
  }
  return c.toDataURL('image/jpeg', 0.9);
}

/** A 16:9 picture of the video, or a plain tile with a play mark when there is none. */
function thumb(config, video, { badge = true } = {}) {
  const box = el('div', 'vl2thumb');
  const url = mediaUrl(config, video?.thumb_path);
  if (url) {
    const img = el('img'); img.src = url; img.alt = ''; img.loading = 'lazy'; img.decoding = 'async';
    img.onerror = () => { img.remove(); box.classList.add('is-blank'); box.prepend(icon('play', 26)); };
    box.appendChild(img);
  } else { box.classList.add('is-blank'); box.appendChild(icon('play', 26)); }
  if (badge && video?.duration_s) box.appendChild(el('span', 'vl2thumb__len', clock(video.duration_s)));
  return box;
}

const plural = (n, one, many = one + 's') => `${n} ${n === 1 ? one : many}`;
const pct = (n, of) => of > 0 ? Math.round((n / of) * 100) : 0;

/* ── the pane ───────────────────────────────────────────────────────────── */

export function videoLinksPane() {
  const pane = el('div', 'vl vl2');
  const body = el('div');
  pane.appendChild(body);
  let overview = null;

  const loading = () => {
    body.textContent = '';
    const l = el('div', 'vl2loading');
    for (let i = 0; i < 3; i++) l.appendChild(el('i'));
    body.appendChild(l);
  };
  const failed = (e, retry) => {
    body.textContent = '';
    const box = el('div', 'vl2empty');
    box.append(el('p', 'vl2empty__t', 'Could not load'), el('p', 'vl2empty__s', e.message));
    const again = btn('Try again', '', 'refresh'); again.onclick = retry;
    box.appendChild(again);
    body.appendChild(box);
  };

  /* ═══ the home: campaigns and videos ═════════════════════════════════════ */
  const showList = async () => {
    closeMenu();
    loading();
    try { overview = await vlOverview(); } catch (e) { failed(e, showList); return; }
    body.textContent = '';
    const { config, videos, campaigns } = overview;

    const head = el('div', 'vl2head');
    const titles = el('div');
    titles.append(el('h2', 'vl2h1', 'Video links'),
      el('p', 'vl2sub', 'Send a video to each person with a link of their own, and see who watched.'));
    const actions = el('div', 'vl2head__act');
    const up = btn('Upload video', '', 'upload'); up.onclick = () => openUpload();
    const nc = btn('New campaign', 'btn--pri', 'plus'); nc.onclick = () => openNewCampaign();
    actions.append(up, nc);
    head.append(titles, actions);
    body.appendChild(head);

    if (!config.media_base_url)
      body.appendChild(el('p', 'note note--warn',
        'The video host is not set up yet (VL_MEDIA_BASE_URL on the video-links-admin function). Links work once it is; thumbnails and the watch page need it for real sends.'));

    /* campaigns */
    body.appendChild(el('h3', 'vl2h2', 'Campaigns'));
    const grid = el('div', 'vl2grid');
    for (const c of campaigns) {
      const v = videos.find(x => x.id === c.video_id);
      const card = el('div', 'vl2card');
      card.tabIndex = 0; card.setAttribute('role', 'button');
      card.setAttribute('aria-label', `Open ${c.name}`);
      card.appendChild(thumb(config, v));
      const info = el('div', 'vl2card__b');
      info.append(el('div', 'vl2card__t', c.name), el('div', 'vl2card__s', v ? v.title : 'video'));
      const nums = el('div', 'vl2card__n');
      nums.append(el('span', null, plural(c.links, 'person', 'people')), el('span', 'vl2dot'), el('span', null, `${c.sent} sent`));
      const bar = el('div', 'vl2bar'); const fill = el('i'); fill.style.width = pct(c.sent, c.links) + '%'; bar.appendChild(fill);
      info.append(nums, bar);
      card.appendChild(info);
      const open = () => showCampaign(c.id);
      card.onclick = open;
      card.onkeydown = e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } };
      grid.appendChild(card);
    }
    const add = el('button', 'vl2card vl2card--add'); add.type = 'button';
    add.append(icon('plus', 22), el('span', null, 'New campaign'));
    add.onclick = () => openNewCampaign();
    grid.appendChild(add);
    body.appendChild(grid);
    if (!campaigns.length)
      body.appendChild(el('p', 'vl2hint', videos.length
        ? 'A campaign is one video sent to a group of people, each with their own link.'
        : 'Start by uploading a video. Then make a campaign for it and add the people you want to send it to.'));

    /* videos */
    body.appendChild(el('h3', 'vl2h2', 'Videos'));
    const vgrid = el('div', 'vl2grid vl2grid--s');
    for (const v of videos) {
      const n = campaigns.filter(c => c.video_id === v.id).length;
      const card = el('div', 'vl2card vl2card--static');
      card.appendChild(thumb(config, v));
      const info = el('div', 'vl2card__b');
      info.append(el('div', 'vl2card__t', v.title), el('div', 'vl2card__s', n ? plural(n, 'campaign') : 'No campaigns yet'));
      card.appendChild(info);
      vgrid.appendChild(card);
    }
    const addv = el('button', 'vl2card vl2card--add'); addv.type = 'button';
    addv.append(icon('upload', 22), el('span', null, 'Upload video'));
    addv.onclick = () => openUpload();
    vgrid.appendChild(addv);
    body.appendChild(vgrid);
  };

  /* upload: the form from video-upload.js, in a dialog */
  const openUpload = () => {
    const wrap = el('div');
    let dlg;
    const done = () => { dlg.close(); showList(); };
    const manual = () => {
      /* The manual fields: for files put in the bucket some other way, or while the
         browser upload is not set up. */
      const m = el('div', 'vl2manual');
      m.appendChild(el('p', 'note', 'Upload the MP4 and the thumbnail to the media bucket first, then enter their paths inside the bucket. The thumbnail should already have its play button drawn on it.'));
      const vt = input('', 'Title'), vp = input('', 'v/…/film.mp4'), tp = input('', 't/…/film.jpg'), vd = input('', 'seconds', 'number');
      const g = el('div', 'vl2form2'); g.append(field('Title', vt), field('Video file path', vp), field('Thumbnail path', tp), field('Length (seconds)', vd));
      const add = btn('Add video', 'btn--pri');
      add.onclick = async () => {
        add.disabled = true;
        try {
          await vlCreateVideo({ title: vt.value, video_path: vp.value, thumb_path: tp.value, duration_s: Number(vd.value) });
          toast('Video added.'); done();
        } catch (e) { toast(e.message, true); add.disabled = false; }
      };
      m.append(g, add);
      return m;
    };
    if (overview?.config?.upload_enabled) {
      wrap.appendChild(videoUploadBox({ onDone: () => done() }));
      const det = el('details', 'vl__manual'); det.appendChild(el('summary', null, 'Already uploaded it another way? Enter the paths'));
      det.appendChild(manual()); wrap.appendChild(det);
    } else {
      wrap.appendChild(el('p', 'note note--warn', 'Uploading from the browser is not set up yet (the R2_* secrets on the video-links-admin function), so the files have to be put in the bucket by hand. See VIDEO-LINKS.md.'));
      wrap.appendChild(manual());
    }
    dlg = dialog('Upload a video', wrap, { wide: true });
  };

  /* a new campaign, in a dialog */
  const openNewCampaign = () => {
    const videos = overview?.videos || [];
    const wrap = el('div', 'vl2form');
    if (!videos.length) {
      wrap.append(el('p', 'vl2hint', 'A campaign sends one video. Upload a video first.'));
      const up = btn('Upload a video', 'btn--pri', 'upload');
      const dlg0 = dialog('New campaign', wrap);
      up.onclick = () => { dlg0.close(); openUpload(); };
      wrap.appendChild(up);
      return;
    }
    const name = input('', 'For example “Spring outreach”');
    const sel = el('select', 'inp');
    for (const v of videos) { const o = el('option', null, v.title); o.value = v.id; sel.appendChild(o); }
    const label = input('', 'For example “Book a call”');
    const url = input('', 'https://…');
    const go = btn('Create campaign', 'btn--pri');
    const dlg = dialog('New campaign', wrap);
    wrap.append(
      field('Name', name), field('Video', sel),
      el('p', 'vl2f__sec', 'A button under the video (optional)'),
      el('p', 'vl2hint', 'It is shown on every person’s page, and presses are counted per person. You can set it later.'),
      field('Button label', label), field('Opens', url));
    go.onclick = async () => {
      go.disabled = true;
      try {
        const payload = { name: name.value, video_id: sel.value };
        if (label.value.trim() || url.value.trim()) { payload.cta_label = label.value; payload.cta_url = url.value; }
        const { campaign } = await vlCreateCampaign(payload);
        dlg.close(); showCampaign(campaign.id);
      } catch (e) { toast(e.message, true); go.disabled = false; }
    };
    const row = el('div', 'vl2dlg__act'); row.appendChild(go); wrap.appendChild(row);
    name.focus();
  };

  /* ═══ one campaign ═══════════════════════════════════════════════════════ */
  let includeBots = false;

  const showCampaign = async (id) => {
    closeMenu();
    loading();
    let d;
    try { d = await vlCampaign(id, includeBots); } catch (e) { failed(e, () => showCampaign(id)); return; }
    paintCampaign(id, d);
  };

  function paintCampaign(id, d) {
    body.textContent = '';
    const { campaign, links, config } = d;
    const video = campaign.video;
    const hasCta = !!(campaign.cta_label && campaign.cta_url);
    let filter = 'all', query = '';

    /* — top bar and header — */
    const crumbs = el('div', 'vl2crumbs');
    const back = btn('Campaigns', 'btn--gh', 'back'); back.onclick = showList;
    const refresh = iconBtn('refresh', 'Refresh the numbers', 'btn--gh'); refresh.onclick = () => showCampaign(id);
    crumbs.append(back, refresh);
    body.appendChild(crumbs);

    const head = el('div', 'vl2chead');
    head.appendChild(thumb(config, video));
    const who = el('div', 'vl2chead__t');
    who.append(el('h2', 'vl2h1', campaign.name),
      el('p', 'vl2sub', `${video?.title || 'video'}${video?.duration_s ? ' · ' + clock(video.duration_s) : ''}`));
    head.appendChild(who);
    const act = el('div', 'vl2head__act');
    const addPeople = btn('Add people', 'btn--pri', 'plus'); addPeople.onclick = () => openAddPeople(id);
    const more = iconBtn('more', 'More', 'btn--gh');
    more.onclick = () => openMenu(more, [
      { label: 'Export CSV', disabled: !links.length, run: () => exportCsv(campaign, links, config) },
      { label: (includeBots ? '✓ ' : '') + 'Show suspected scanners and bots', run: () => { includeBots = !includeBots; showCampaign(id); } },
      '-',
      { label: 'Delete campaign…', danger: true, run: async () => {
        if (!confirm(`Delete “${campaign.name}”, its ${plural(links.length, 'link')} and everything recorded for them?`)) return;
        try { await vlDeleteCampaign(id); showList(); } catch (e) { toast(e.message, true); }
      } }
    ]);
    act.append(addPeople, more);
    head.appendChild(act);
    body.appendChild(head);

    /* — the numbers — */
    const stats = el('div', 'vl2stats');
    body.appendChild(stats);
    const paintStats = () => {
      stats.textContent = '';
      const s = d.summary, total = links.length;
      const sent = links.filter(l => l.status === 'sent').length;
      const cards = [
        ['Sent', sent, total ? `of ${total}` : '', pct(sent, total), 'sent'],
        ['Opened', s.clicked, `${pct(s.clicked, total)}% of ${total}`, pct(s.clicked, total), 'opened'],
        ['Watched', s.started, `${pct(s.started, total)}% of ${total}`, pct(s.started, total), 'watched'],
        ['Finished', s.finished, `${pct(s.finished, total)}% of ${total}`, pct(s.finished, total), 'finished']
      ];
      if (hasCta) cards.push(['Pressed the button', s.cta ?? 0, `${pct(s.cta ?? 0, total)}% of ${total}`, pct(s.cta ?? 0, total), 'cta']);
      for (const [k, n, sub, p, tone] of cards) {
        const c = el('div', `vl2stat vl2stat--${tone}`);
        const bar = el('div', 'vl2bar'); const fill = el('i'); fill.style.width = p + '%'; bar.appendChild(fill);
        c.append(el('span', 'vl2stat__k', k), el('b', null, String(n)), el('span', 'vl2stat__s', sub || ' '), bar);
        stats.appendChild(c);
      }
    };
    paintStats();

    /* — who kept watching, and the message — */
    const two = el('div', 'vl2two');
    two.append(retentionCard(d.summary), messageCard(id, campaign, () => showCampaign(id)));
    body.appendChild(two);

    /* — the people — */
    const people = el('div', 'vl2people');
    body.appendChild(people);
    const bar = el('div', 'vl2tools');
    const find = el('label', 'vl2find'); find.appendChild(icon('search', 15));
    const q = el('input'); q.type = 'search'; q.placeholder = 'Search people'; q.setAttribute('aria-label', 'Search people');
    find.appendChild(q);
    const chips = el('div', 'vl2chips');
    bar.append(find, chips);
    people.append(el('h3', 'vl2h2', `People · ${links.length}`), bar);
    const list = el('div', 'vl2list');
    people.appendChild(list);

    const hidden = links.reduce((n, l) => n + l.stats.suspected_scanner_loads, 0);
    if (hidden && !includeBots) people.appendChild(el('p', 'vl2foot', `${plural(hidden, 'page load')} left out as likely link scanners or bots.`));

    const paintChips = () => {
      chips.textContent = '';
      for (const [key, label] of LINK_FILTERS) {
        if (key === 'cta' && !hasCta) continue;
        const n = links.filter(l => matchesFilter(l, key)).length;
        if (key !== 'all' && key !== filter && n === 0) continue;
        const c = el('button', 'vl2chip' + (key === filter ? ' is-on' : ''));
        c.type = 'button'; c.append(el('span', null, label), el('em', null, String(n)));
        c.onclick = () => { filter = key; paintChips(); paintRows(); };
        chips.appendChild(c);
      }
    };

    const open = new Set();
    const paintRows = () => {
      list.textContent = '';
      const shown = byRecentActivity(links).filter(l => matchesFilter(l, filter) && matchesQuery(l, query));
      if (!links.length) {
        const e = el('div', 'vl2empty');
        e.append(el('p', 'vl2empty__t', 'No one here yet'), el('p', 'vl2empty__s', 'Add people to make a personal link for each of them.'));
        const b = btn('Add people', 'btn--pri', 'plus'); b.onclick = () => openAddPeople(id); e.appendChild(b);
        list.appendChild(e); return;
      }
      if (!shown.length) { list.appendChild(el('p', 'vl2empty__s vl2nomatch', 'No one matches.')); return; }
      for (const l of shown) list.appendChild(personRow(l));
    };

    const afterChange = () => { paintStats(); paintChips(); paintRows(); };

    function personRow(l) {
      const s = l.stats, link = watchUrl(config, l.token), st = linkStatus(l);
      const wrap = el('div', 'vl2row' + (open.has(l.id) ? ' is-open' : ''));
      const main = el('div', 'vl2row__main');
      main.tabIndex = 0; main.setAttribute('role', 'button'); main.setAttribute('aria-expanded', open.has(l.id) ? 'true' : 'false');
      const toggle = () => { open.has(l.id) ? open.delete(l.id) : open.add(l.id); paintRows(); };
      main.onclick = e => { if (!e.target.closest('button')) toggle(); };
      main.onkeydown = e => { if ((e.key === 'Enter' || e.key === ' ') && e.target === main) { e.preventDefault(); toggle(); } };

      const av = el('span', 'vl2av', initials(l.recipient_name));
      av.style.setProperty('--h', String(hueFor(l.recipient_email)));
      const who = el('div', 'vl2who');
      who.append(el('b', null, l.recipient_name), el('span', null, l.recipient_email));
      const person = el('div', 'vl2person'); person.append(av, who);

      /* The status is also the switch for Sent: a person who has not opened it
         yet is "Not sent" or "Sent", and a click moves between the two. */
      const canToggle = st.key === 'draft' || st.key === 'sent';
      const pill = el(canToggle ? 'button' : 'span', `vl2pill vl2pill--${st.key}`, st.label);
      if (canToggle) {
        pill.type = 'button';
        pill.title = st.key === 'sent' ? 'Sent. Click to mark as not sent' : 'Click to mark as sent';
        pill.onclick = () => toggleSent(l);
      }
      const statusCell = el('div', 'vl2status'); statusCell.appendChild(pill);
      if ((s.cta_clicks || 0) > 0) {
        const b = el('span', 'vl2badge'); b.title = `Pressed the button ${plural(s.cta_clicks, 'time')}`;
        b.append(icon('cursor', 12), el('span', null, 'Button'));
        statusCell.appendChild(b);
      }

      const prog = el('div', 'vl2prog');
      const track = el('div', 'vl2bar'); const fill = el('i'); fill.style.width = (s.started ? s.furthest_pct : 0) + '%'; track.appendChild(fill);
      prog.appendChild(track);
      prog.appendChild(el('span', null, s.started ? `${clock(s.unique_seconds)} watched` : s.visits ? 'Not played' : ''));

      const last = el('div', 'vl2last', s.last_activity_at ? relativeTime(s.last_activity_at, Date.now(), formatDate) : (l.sent_at ? 'sent ' + relativeTime(l.sent_at, Date.now(), formatDate) : ''));
      if (s.last_activity_at) last.title = formatDateTime(new Date(s.last_activity_at));

      const acts = el('div', 'vl2acts');
      const copyEmail = btn('Copy email', 'btn--pri', 'mail', 'The thumbnail, linked to this person’s page, ready to paste into a mail');
      copyEmail.onclick = () => doCopyEmail(l, link);
      const copyLink = iconBtn('link', 'Copy link', 'btn--gh');
      copyLink.onclick = async () => { try { await navigator.clipboard.writeText(link); toast('Link copied.'); } catch (_) { toast('Could not copy.', true); } };
      const menuBtn = iconBtn('more', 'More', 'btn--gh');
      menuBtn.onclick = () => openMenu(menuBtn, [
        { label: 'Copy with name on the picture', disabled: !mediaUrl(config, video?.thumb_path), run: () => doCopyNamed(l, link) },
        { label: l.status === 'sent' ? 'Mark as not sent' : 'Mark as sent', run: () => toggleSent(l) },
        { label: l.expires_at ? `Expires ${formatDate(new Date(l.expires_at))}…` : 'Set an expiry date…', run: () => setExpiry(l) },
        '-',
        { label: 'Delete this person…', danger: true, run: () => deletePerson(l) }
      ]);
      acts.append(copyEmail, copyLink, menuBtn);

      main.append(person, statusCell, prog, last, acts);
      wrap.appendChild(main);
      if (open.has(l.id)) wrap.appendChild(personDetails(l, link));
      return wrap;
    }

    function personDetails(l, link) {
      const s = l.stats;
      const box = el('div', 'vl2det');
      const facts = el('dl', 'vl2facts');
      const add = (k, v) => {
        if (v === '' || v == null) return;
        const f = el('div'); f.append(el('dt', null, k), el('dd', null, String(v)));
        facts.appendChild(f);
      };
      add('Link made', l.created_at ? formatDate(new Date(l.created_at)) : '');
      add('Sent', l.sent_at ? formatDateTime(new Date(l.sent_at)) : 'Not marked as sent');
      add('First opened', s.first_clicked_at ? formatDateTime(new Date(s.first_clicked_at)) : 'Not yet');
      add('Last activity', s.last_activity_at ? formatDateTime(new Date(s.last_activity_at)) : '');
      add('Visits', s.visits);
      add('Seconds watched', s.unique_seconds ? `${clock(s.unique_seconds)}${video?.duration_s ? ' of ' + clock(video.duration_s) : ''}` : '');
      add('Furthest', s.started ? s.furthest_pct + '%' : '');
      if (hasCta) add('Button presses', s.cta_clicks || 0);
      add('Device', [s.device, s.country].filter(Boolean).join(' · '));
      add('Expires', l.expires_at ? formatDate(new Date(l.expires_at)) : 'Never');
      box.appendChild(facts);
      const lr = el('div', 'vl2linkrow');
      const li = el('input', 'inp'); li.readOnly = true; li.value = link; li.setAttribute('aria-label', 'Personal link');
      li.onfocus = () => li.select();
      lr.appendChild(li);
      box.appendChild(lr);
      return box;
    }

    const toggleSent = async (l) => {
      const to = l.status !== 'sent';
      try {
        await vlMarkSent(l.id, to);
        l.status = to ? 'sent' : 'created'; l.sent_at = to ? new Date().toISOString() : null;
        afterChange();
      } catch (e) { toast(e.message, true); }
    };
    const setExpiry = async (l) => {
      const ymd = await pickDate({ title: 'Link expires', value: l.expires_at ? l.expires_at.slice(0, 10) : null });
      if (ymd === undefined) return;
      try {
        const iso = ymd ? new Date(ymd + 'T23:59:59').toISOString() : null;
        await vlSetExpiry(l.id, iso); l.expires_at = iso; afterChange();
      } catch (e) { toast(e.message, true); }
    };
    const deletePerson = async (l) => {
      if (!confirm(`Delete ${l.recipient_name} (${l.recipient_email}) and everything recorded for them, in every campaign?`)) return;
      try { await vlDeleteRecipient(l.recipient_email); showCampaign(id); } catch (e) { toast(e.message, true); }
    };
    const thumbUrl = mediaUrl(config, video?.thumb_path);
    const textFor = (l, link) => campaign.email_template ? fillTemplate(campaign.email_template, { name: l.recipient_name, link }) : '';
    const doCopyEmail = async (l, link) => {
      const r = await copyRich(emailClipboard({ link, thumbUrl, text: textFor(l, link) }));
      toast(r === true ? (thumbUrl ? 'Copied — paste it into your mail.' : 'Copied the link only: no thumbnail is set up yet.') : r === 'plain' ? 'Copied as plain text.' : 'Could not copy.', r !== true);
    };
    const doCopyNamed = async (l, link) => {
      try {
        const dataUrl = await personalThumbnail(thumbUrl, l.recipient_name);
        const r = await copyRich(emailClipboard({ link, thumbUrl: dataUrl, text: textFor(l, link) }));
        toast(r === true ? 'Copied with the name on the picture — check how it pastes into your mail.' : 'Could not copy.', r !== true);
      } catch (e) { toast(e.message, true); }
    };

    q.oninput = () => { query = q.value; paintRows(); };
    paintChips(); paintRows();
  }

  const exportCsv = (campaign, links, config) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([recipientsCsv(links, config)], { type: 'text/csv' }));
    a.download = `${campaign.name.replace(/[^\w.-]+/g, '_')}-links.csv`;
    a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  };

  /* add people: a dialog with the paste box */
  const openAddPeople = (campaignId) => {
    const wrap = el('div', 'vl2form');
    wrap.appendChild(el('p', 'vl2hint', 'One per line: “Name, email”, “Name <email>” or just an address. Anyone who already has a link for this video is skipped.'));
    const ta = el('textarea', 'inp vl2ta'); ta.rows = 7; ta.placeholder = 'Anna Virtanen, anna@example.com\nBen Koski <ben@example.com>';
    const go = btn('Make links', 'btn--pri');
    const dlg = dialog('Add people', wrap);
    go.onclick = async () => {
      go.disabled = true;
      try {
        const r = await vlAddRecipients(campaignId, ta.value);
        const notes = [];
        if (r.already_have_link.length) notes.push(`${r.already_have_link.length} already had a link`);
        if (r.duplicates_in_paste.length) notes.push(`${r.duplicates_in_paste.length} repeated in the paste`);
        if (r.rejected.length) notes.push(`${r.rejected.length} had no readable address (${r.rejected.slice(0, 3).join('; ')})`);
        toast(`${plural(r.created, 'link')} made.${notes.length ? ' ' + notes.join(', ') + '.' : ''}`, r.rejected.length > 0);
        dlg.close(); showCampaign(campaignId);
      } catch (e) { toast(e.message, true); go.disabled = false; }
    };
    const row = el('div', 'vl2dlg__act'); row.appendChild(go);
    wrap.append(ta, row);
    ta.focus();
  };

  /* ── cards on a campaign ──────────────────────────────────────────────── */

  /** "Who kept watching": how many of those who pressed play were still there at each tenth. */
  function retentionCard(summary) {
    const card = el('section', 'vl2panel');
    card.appendChild(el('h3', 'vl2panel__h', 'Who kept watching'));
    const pts = retentionPoints(summary);
    if (!pts.length) {
      card.appendChild(el('p', 'vl2empty__s vl2chartempty', 'Nobody has pressed play yet. The chart fills in as people watch.'));
      return card;
    }
    const W = 560, H = 170, L = 30, R = 10, T = 12, B = 24, max = Math.max(1, summary.started);
    const x = i => L + ((W - L - R) * i) / 10, y = n => T + (H - T - B) * (1 - n / max);
    const line = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.n).toFixed(1)}`).join(' ');
    const area = `${line} L${x(10).toFixed(1)},${(H - B).toFixed(1)} L${x(0).toFixed(1)},${(H - B).toFixed(1)} Z`;
    const grid = [0, 0.5, 1].map(f => {
      const yy = (T + (H - T - B) * (1 - f)).toFixed(1);
      return `<line x1="${L}" x2="${W - R}" y1="${yy}" y2="${yy}" class="g"/><text x="${L - 6}" y="${Number(yy) + 3.5}" text-anchor="end">${Math.round(max * f * 10) / 10}</text>`;
    }).join('');
    const labels = [0, 2, 4, 6, 8, 10].map(i => `<text x="${x(i).toFixed(1)}" y="${H - 6}" text-anchor="middle">${i * 10}%</text>`).join('');
    const dots = pts.map((p, i) => `<circle cx="${x(i).toFixed(1)}" cy="${y(p.n).toFixed(1)}" r="3.2"><title>${p.n} of ${summary.started} reached ${p.pct}%</title></circle>`).join('');
    const svg = el('div', 'vl2chart');
    svg.innerHTML = `<svg viewBox="0 0 ${W} ${H}" role="img" aria-label="People still watching at each tenth of the video">${grid}${labels}<path d="${area}" class="a"/><path d="${line}" class="l"/>${dots}</svg>`;
    card.appendChild(svg);
    const end = pts[10].n;
    card.appendChild(el('p', 'vl2panel__s', `${summary.started} pressed play · ${end} watched to the end`));
    return card;
  }

  /** The email text and the button under the video: a summary, and a dialog to change them. */
  function messageCard(id, campaign, onSaved) {
    const card = el('section', 'vl2panel vl2msg');
    const top = el('div', 'vl2panel__top');
    top.appendChild(el('h3', 'vl2panel__h', 'Message and button'));
    const edit = btn('Edit', 'btn--gh'); edit.onclick = () => openMessageEditor(id, campaign, onSaved);
    top.appendChild(edit);
    card.appendChild(top);

    const row = (label, ...nodes) => { const r = el('div', 'vl2msg__r'); r.appendChild(el('span', 'vl2msg__k', label)); const v = el('div', 'vl2msg__v'); v.append(...nodes); r.appendChild(v); return r; };
    const text = (campaign.email_template || '').trim();
    card.appendChild(row('Email text', text
      ? el('p', 'vl2msg__t', text)
      : el('p', 'vl2msg__none', 'None. “Copy email” copies just the picture.')));
    if (campaign.cta_label && campaign.cta_url) {
      const b = el('span', 'vl2ctabtn vl2ctabtn--s', campaign.cta_label);
      card.appendChild(row('Button under the video', b, el('p', 'vl2msg__url', campaign.cta_url)));
    } else {
      card.appendChild(row('Button under the video', el('p', 'vl2msg__none', 'None. Add one to send people somewhere after they watch, and see who pressed it.')));
    }
    return card;
  }

  const openMessageEditor = (id, campaign, onSaved) => {
    const wrap = el('div', 'vl2form');
    const ta = el('textarea', 'inp vl2ta'); ta.rows = 6; ta.value = campaign.email_template || '';
    ta.placeholder = 'Hi {first_name},\n\nI made something for you — press the picture below to watch it.';
    const label = input(campaign.cta_label || '', 'For example “Book a call”');
    const url = input(campaign.cta_url || '', 'https://…');
    const preview = el('div', 'vl2ctaprev');
    const prevBtn = el('span', 'vl2ctabtn');
    preview.append(el('span', 'vl2f__l', 'How it looks on the page'), prevBtn);
    const paintPrev = () => { prevBtn.textContent = label.value.trim() || 'Your button'; preview.classList.toggle('is-off', !label.value.trim()); };
    label.oninput = paintPrev; paintPrev();

    const save = btn('Save', 'btn--pri');
    const dlg = dialog('Message and button', wrap);
    save.onclick = async () => {
      const patch = { email_template: ta.value };
      // The button is only sent when it changed, so the text can still be saved before the database has the button columns.
      const changed = label.value.trim() !== (campaign.cta_label || '') || url.value.trim() !== (campaign.cta_url || '');
      if (changed) { patch.cta_label = label.value; patch.cta_url = url.value; }
      save.disabled = true;
      try {
        await vlUpdateCampaign(id, patch);
        toast('Saved.');
        dlg.close(); onSaved();
      } catch (e) { toast(e.message, true); save.disabled = false; }
    };
    const row = el('div', 'vl2dlg__act'); row.appendChild(save);
    wrap.append(
      field('Email text', ta, 'Copied above the thumbnail by “Copy email”. {first_name}, {name} and {link} are filled in for each person. You send the mail yourself.'),
      el('p', 'vl2f__sec', 'Button under the video'),
      el('p', 'vl2hint', 'Shown on every person’s page, and presses are counted per person. Leave both empty for no button.'),
      field('Label', label), field('Opens', url, 'https://… or an email address.'),
      preview, row);
  };

  /* NOT on mount. The Admin page builds every tab's pane up front, and this one
     asks the server for data — so it waits until its tab is first opened
     (ui/admin.js calls pane.load), rather than querying on every admin page load. */
  let loaded = false;
  pane.load = () => { if (!loaded) { loaded = true; showList(); } };
  return pane;
}
